import { useState, useRef, useEffect } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Bot } from "lucide-react";
import { API } from "@/lib/apiClient";
import { useAI } from "@/context/AIContext";

const SUGGESTIONS = [
  "Best motor for a 5kg robot arm joint?",
  "Compare ToF vs ultrasonic distance sensors",
  "Find a 48V driver for a BLDC motor",
];

export default function AISourcingChat({ open, onOpenChange }) {
  const { pending, clearPending } = useAI();
  const [sessionId] = useState(() => `sess-${Math.random().toString(36).slice(2)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  useEffect(() => {
    if (open && pending && !streaming) {
      const p = pending;
      clearPending();
      send(p);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pending]);

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: content }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "ROSA AI is temporarily unavailable. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-l border-[#1C2A38]/10 bg-[var(--surface)] p-0 text-[#1C2A38] sm:max-w-md"
        data-testid="ai-chat-panel"
      >
        <SheetHeader className="border-b border-[#1C2A38]/10 px-5 py-4">
          <SheetTitle className="flex items-center gap-2.5 text-[#1C2A38]">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-[var(--teal-soft)]">
              <Sparkles className="h-4 w-4 text-[var(--teal-deep)]" />
            </span>
            <span className="font-display">ROSA AI Sourcing</span>
            <span className="live-dot ml-1 h-2 w-2 rounded-full bg-[var(--teal)]" />
            <span className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--teal-deep)]">Live</span>
          </SheetTitle>
        </SheetHeader>

        <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="rounded-2xl border border-[#1C2A38]/10 bg-[var(--surface-2)] p-4 text-sm leading-relaxed text-[#3c4a5a]">
                Hi — I'm ROSA AI. Tell me what you're building and I'll help you source the right
                components, motors, sensors and verified suppliers.
              </div>
              <div className="space-y-2">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    data-testid="ai-suggestion"
                    className="block w-full rounded-xl border border-[#1C2A38]/10 bg-transparent px-4 py-2.5 text-left text-sm text-[#3c4a5a] transition-colors hover:border-[var(--teal)]/50 hover:bg-[var(--teal-soft)]"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} className={`flex gap-3 ${m.role === "user" ? "justify-end" : ""}`} data-testid={`ai-msg-${m.role}`}>
              {m.role === "assistant" && (
                <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-[var(--teal-soft)]">
                  <Bot className="h-4 w-4 text-[var(--teal-deep)]" />
                </span>
              )}
              <div
                className={`max-w-[82%] whitespace-pre-wrap rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "bg-[var(--teal)] text-[#04221b]"
                    : "border border-[#1C2A38]/10 bg-[var(--surface-2)] text-[#1C2A38]"
                } ${m.role === "assistant" && streaming && i === messages.length - 1 ? "typing-cursor" : ""}`}
              >
                {m.content}
              </div>
            </div>
          ))}
        </div>

        <div className="border-t border-[#1C2A38]/10 p-4">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about parts, specs, suppliers…"
              data-testid="ai-chat-input"
              className="border-[#1C2A38]/15 bg-[var(--surface-2)] text-[#1C2A38] placeholder:text-[#8593a3] focus-visible:ring-[var(--teal)]"
            />
            <Button
              type="submit"
              disabled={streaming || !input.trim()}
              data-testid="ai-chat-send"
              className="bg-[var(--teal)] text-[#063b34] hover:bg-[#1ee9cf]"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </SheetContent>
    </Sheet>
  );
}
